import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

const VAT_MULTIPLIER = 1.23;
const SKELETON_ROWS = ["alpha", "bravo", "charlie"];

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("pt-PT", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(value);

const normalizeNumber = (value: number | null | undefined) => {
	const parsed = Number(value ?? 0);
	return Number.isNaN(parsed) ? 0 : parsed;
};

type UsedMaterialEntry = {
	id?: string | number | null;
	quantity?: number | null;
	name?: string | null;
	storeBox?: string | null;
	price?: number | null;
};

type RepairUsedMaterialsTableProps = {
	materials: UsedMaterialEntry[];
	loading?: boolean;
	isReturnDisabled: boolean;
	arrivedLabel: (material: UsedMaterialEntry) => string;
	onReturn: (material: UsedMaterialEntry) => void;
	onReturnDamaged: (material: UsedMaterialEntry) => void;
	onReturnBroken: (material: UsedMaterialEntry) => void;
};

export function RepairUsedMaterialsTable({
	materials,
	loading = false,
	isReturnDisabled,
	arrivedLabel,
	onReturn,
	onReturnDamaged,
	onReturnBroken,
}: RepairUsedMaterialsTableProps) {
	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow key={`used-material-${rowKey}`} className="animate-pulse">
					<TableCell>
						<div className="h-4 w-10 rounded bg-muted/70" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-40 rounded bg-muted/60" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-12 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/40" />
					</TableCell>
					<TableCell className="text-right">
						<div className="ml-auto h-8 w-40 rounded bg-muted/30" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead className="w-[70px]">Qtn.</TableHead>
						<TableHead>Descrição</TableHead>
						<TableHead className="w-[100px] text-center">Caixa</TableHead>
						<TableHead>Disponivel a</TableHead>
						<TableHead className="w-[100px] text-center">Preço</TableHead>
						<TableHead className="w-[150px] text-center">
							Preço C/ IVA
						</TableHead>
						<TableHead className="w-[300px] text-right">Ações</TableHead>
					</TableRow>
				</TableHeader>
				{loading ? (
					renderSkeleton()
				) : (
					<TableBody>
						{materials.map((material, index) => {
							const price = normalizeNumber(material.price);
							const rowKey =
								material.id ?? `${material.name ?? "material"}-${index}`;

							return (
								<TableRow key={rowKey}>
									<TableCell>{material.quantity ?? "-"}</TableCell>
									<TableCell>{material.name ?? "-"}</TableCell>
									<TableCell className="text-center">
										{material.storeBox ?? "-"}
									</TableCell>
									<TableCell>{arrivedLabel(material)}</TableCell>
									<TableCell className="text-center">
										{formatCurrency(price)}
									</TableCell>
									<TableCell className="text-center">
										{formatCurrency(price * VAT_MULTIPLIER)}
									</TableCell>
									<TableCell className="text-right">
										<div className="flex justify-end gap-2">
											<Button
												variant="secondary"
												size="sm"
												disabled={isReturnDisabled}
												onClick={() => onReturn(material)}
											>
												Devolver
											</Button>
											<Button
												size="sm"
												variant="destructive"
												disabled={isReturnDisabled}
												onClick={() => onReturnDamaged(material)}
											>
												Defeituoso
											</Button>
											<Button
												size="sm"
												variant="secondary"
												disabled={isReturnDisabled}
												onClick={() => onReturnBroken(material)}
											>
												Partido
											</Button>
										</div>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				)}
			</Table>
		</div>
	);
}
